import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, fs } from '../firebase';
import { initializeSyrve } from '../services/syrveClient';
import { SYRVE_CUSTOMER_COMMENT, getConsentStatus } from '../services/syrveLoyaltySync';
import { hasCompletedOnboarding, normalizePhoneNumber, getUserPhoneNumber, analyzeUserPhoneNumbers } from '../utils/userUtils';

function getLast7Digits(phoneNumber) {
    if (!phoneNumber) return null;
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    return digitsOnly.slice(-7);
}

const RequireOnboarding = ({ children }) => {
    const [loading, setLoading] = useState(true);
    const [needsOnboarding, setNeedsOnboarding] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const checkPhoneConsistency = async (userData, user) => {
        try {
            let phoneNumber = getUserPhoneNumber(userData);

            if (!phoneNumber) {
                return { needsOnboarding: true, reason: 'no_phone' };
            }

            phoneNumber = normalizePhoneNumber(phoneNumber);

            console.log('🔍 Checking phone consistency between Firebase and Syrve:', phoneNumber);

            const syrve = await initializeSyrve();

            try {
                const syrveCustomer = await syrve.getCustomerByPhone(phoneNumber);

                if (syrveCustomer && syrveCustomer.phone) {
                    const syrvePhone = normalizePhoneNumber(syrveCustomer.phone);

                    if (syrvePhone !== phoneNumber) {
                        console.log('⚠️ Phone number mismatch detected!');
                        console.log('📱 Firebase phone:', phoneNumber);
                        console.log('📱 Syrve phone:', syrvePhone);
                        console.log('🔄 Clearing phone to force onboarding and sync correct number...');

                        const userRef = doc(fs, 'users', user.uid);
                        await setDoc(userRef, {
                            phoneNumber: null,
                            phone: null,
                            number: null
                        }, { merge: true });

                        return { needsOnboarding: true, reason: 'phone_mismatch' };
                    }

                    console.log('✅ Phone numbers match in Firebase and Syrve');
                }
            } catch (error) {
                console.log('ℹ️ Customer not found in Syrve with this phone, will create new guest');
            }

            return { needsOnboarding: false };
        } catch (error) {
            console.error('❌ Error checking phone consistency:', error);
            return { needsOnboarding: false };
        }
    };

    const resolveDuplicatePhones = async (userData, user, analysis = null) => {
        const phoneAnalysis = analysis || analyzeUserPhoneNumbers(userData);

        if (!phoneAnalysis.hasMultiple) {
            return {
                sanitizedUserData: userData,
                canonicalPhone: phoneAnalysis.canonical || null,
                updated: false,
                analysis: phoneAnalysis
            };
        }

        const canonicalPhone = phoneAnalysis.canonical;
        console.log('⚠️ Multiple phone numbers detected for user:', {
            uid: user.uid,
            email: user.email,
            phones: phoneAnalysis.uniqueNormalized
        });

        if (!canonicalPhone) {
            console.warn('Unable to determine canonical phone number, skipping automatic resolution.');
            return {
                sanitizedUserData: userData,
                canonicalPhone: null,
                updated: false,
                analysis: phoneAnalysis
            };
        }

        const timestamp = new Date().toISOString();
        const fieldsToUpdate = [
            'phoneNumber',
            'number',
            'phone',
            'PhoneNumber',
            'Phone',
            'Number'
        ];

        const updatePayload = {
            updatedAt: timestamp,
            phoneNumberConflictResolvedAt: timestamp
        };

        fieldsToUpdate.forEach((field) => {
            updatePayload[field] = canonicalPhone;
        });

        const userRef = doc(fs, 'users', user.uid);
        await setDoc(userRef, updatePayload, { merge: true });

        if (user.email) {
            try {
                const legacyRef = doc(fs, 'Users', user.email);
                await setDoc(legacyRef, updatePayload, { merge: true });
            } catch (legacyError) {
                console.warn('Failed to update legacy Users collection:', legacyError.message);
            }
        }

        console.log('✅ Resolved duplicate phone numbers. Canonical phone:', canonicalPhone);

        return {
            sanitizedUserData: {
                ...userData,
                ...updatePayload
            },
            canonicalPhone,
            updated: true,
            analysis: phoneAnalysis
        };
    };

    const checkAndCreateSyrveGuest = async (userData, user, overridePhone = null) => {
        try {
            let phoneNumber = overridePhone || getUserPhoneNumber(userData);

            if (!phoneNumber) {
                console.log('⚠️ No phone number to check in Syrve');
                return;
            }

            const originalPhone = phoneNumber;
            phoneNumber = normalizePhoneNumber(phoneNumber);

            if (originalPhone !== phoneNumber) {
                console.log('📞 Updating phone number format from', originalPhone, 'to', phoneNumber);
                const timestamp = new Date().toISOString();
                const updatePayload = {
                    phoneNumber: phoneNumber,
                    number: phoneNumber,
                    phone: phoneNumber,
                    PhoneNumber: phoneNumber,
                    Phone: phoneNumber,
                    Number: phoneNumber,
                    updatedAt: timestamp,
                    phoneNumberNormalizedAt: timestamp
                };

                const userRef = doc(fs, 'users', user.uid);
                await setDoc(userRef, updatePayload, { merge: true });

                if (user.email) {
                    try {
                        await setDoc(doc(fs, 'Users', user.email), updatePayload, { merge: true });
                    } catch (legacyError) {
                        console.warn('Failed to normalize phone in legacy Users collection:', legacyError.message);
                    }
                }

                console.log('✅ Phone number format updated in Firebase');
            }

            console.log('🔍 Checking if user exists in Syrve with phone:', phoneNumber);

            const syrve = await initializeSyrve();

            let syrveCustomer = null;
            try {
                syrveCustomer = await syrve.getCustomerByPhone(phoneNumber);
                console.log('✅ User found in Syrve:', syrveCustomer?.id);
            } catch (error) {
                console.log('⚠️ User not found in Syrve, will create new guest');
            }

            if (!syrveCustomer || !syrveCustomer.id) {
                console.log('🆕 Creating guest in Syrve...');

                let birthday = null;
                if (userData.dateOfBirth) {
                    try {
                        const dateObj = new Date(userData.dateOfBirth);
                        if (!isNaN(dateObj.getTime())) {
                            birthday = dateObj.toISOString().replace('T', ' ').split('.')[0] + '.000';
                        }
                    } catch (e) {
                        console.warn('Failed to parse date of birth:', e);
                    }
                }

                const last7Digits = getLast7Digits(phoneNumber);
                const cardNumber = last7Digits;

                const customerData = {
                    id: null,
                    phone: phoneNumber || null,
                    cardTrack: cardNumber || "9",
                    cardNumber: cardNumber,
                    name: userData.firstName || userData.name,
                    surName: userData.lastName,
                    birthday,
                    email: user.email,
                    userData: userData.personalId,
                    consentStatus: getConsentStatus(userData),
                    comment: SYRVE_CUSTOMER_COMMENT
                };

                Object.keys(customerData).forEach(key => {
                    if (key === 'id') return;
                    const value = customerData[key];
                    if (value === undefined || value === null || value === '' ||
                        (typeof value === 'string' && value.trim() === '')) {
                        delete customerData[key];
                    }
                });

                const createdCustomer = await syrve.createOrUpdateCustomer(customerData);
                console.log('✅ Guest created in Syrve:', createdCustomer?.id);

                if (createdCustomer && createdCustomer.id) {
                    const userRef = doc(fs, 'users', user.uid);
                    await setDoc(userRef, {
                        syrveCustomerId: createdCustomer.id,
                        cardNumber: cardNumber,
                        syrveSyncedAt: new Date(),
                        syrveOrgId: syrve.organizationId
                    }, { merge: true });

                    console.log('✅ Syrve customer ID and card number saved to Firebase');
                }
            }
        } catch (error) {
            console.error('❌ Error checking/creating Syrve guest:', error);
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setIsAuthenticated(true);

                try {
                    const userDoc = await getDoc(doc(fs, 'users', user.uid));
                    const rawUserData = userDoc.exists() ? userDoc.data() : null;

                    let userData = rawUserData;
                    let forceOnboarding = false;
                    let canonicalPhoneForSync = null;

                    const phoneAnalysis = analyzeUserPhoneNumbers(rawUserData);

                    if (phoneAnalysis.hasMultiple) {
                        forceOnboarding = true;
                        console.log('🚨 Multiple phone numbers detected, forcing onboarding...');
                        const resolution = await resolveDuplicatePhones(rawUserData, user, phoneAnalysis);
                        userData = resolution.sanitizedUserData;
                        canonicalPhoneForSync = resolution.canonicalPhone;

                        if (canonicalPhoneForSync) {
                            await checkAndCreateSyrveGuest(userData, user, canonicalPhoneForSync);
                        }
                    }

                    const needsOnboardingCheck = !hasCompletedOnboarding(userData);

                    if (needsOnboardingCheck || forceOnboarding) {
                        console.log('🚨 User needs onboarding:', {
                            hasData: !!userData,
                            hasPhone: !!getUserPhoneNumber(userData),
                            forceOnboardingReason: forceOnboarding ? 'multiple_phone_numbers' : 'missing_phone'
                        });
                        setNeedsOnboarding(true);
                    } else {
                        const consistencyCheck = await checkPhoneConsistency(userData, user);
                        if (consistencyCheck.needsOnboarding) {
                            console.log('🚨 Phone inconsistency detected, needs onboarding:', consistencyCheck.reason);
                            setNeedsOnboarding(true);
                        } else {
                            console.log('✅ User onboarding complete and phone numbers consistent');
                            setNeedsOnboarding(false);
                            checkAndCreateSyrveGuest(userData, user);
                        }
                    }
                } catch (error) {
                    console.error('Error checking onboarding status:', error);
                    setNeedsOnboarding(true);
                }
            } else {
                setIsAuthenticated(false);
                setNeedsOnboarding(false);
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh'
            }}>
                <div className="spinner"></div>
            </div>
        );
    }

    // If not authenticated, just show the content (don't force login)
    if (!isAuthenticated) {
        return children;
    }

    // Only redirect to onboarding if user IS authenticated but hasn't completed onboarding
    if (isAuthenticated && needsOnboarding) {
        return <Navigate to="/onboarding" replace />;
    }

    return children;
};

export default RequireOnboarding;
